import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { db } from '../db';
import { API } from '@/config';
import { COUNTRY_CODES } from '../constants/common';

export function useDoctorSearch(token) {
    const [isSearching, setIsSearching] = useState(false);
    const [searchComplete, setSearchComplete] = useState(false);
    const [searchResults, setSearchResults] = useState(null);


    const searchDoctorByPhone = useCallback(async (phone) => {
        if (!phone || phone.length < 10) return;

        setIsSearching(true);
        setSearchResults(null);

        try {
            // 1. Check Local DB
            const localDoc = await db.users.where('phone').equals(phone).first();
            if (localDoc) {
                setSearchResults({
                    found: true,
                    source: 'local',
                    doctor: localDoc
                });
                toast.success("Doctor found in your contact list!");
                setSearchComplete(true);
                setIsSearching(false);
                return;
            }

            // 2. Check Global API
            if (token) {
                try {
                    const response = await axios.get(`${API}/users/search?phone=${encodeURIComponent(phone)}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (response.data && response.data.length > 0) {
                        const globalDoc = response.data[0];
                        setSearchResults({
                            found: true,
                            source: 'global',
                            doctor: globalDoc
                        });
                        toast.success("Doctor found on CareTrack!");
                    } else {
                        setSearchResults({
                            found: false,
                            source: 'none'
                        });
                        toast.info("New doctor. Please enter name.");
                    }
                } catch (err) {
                    console.error("Global doctor search failed", err);
                    // Don't toast error here to avoid blocking UI, just treat as not found or fallback
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsSearching(false);
            setSearchComplete(true);
        }
    }, [token]);

    // Helper to extract local number
    const getLocalNumber = useCallback((phone) => {
        if (!phone) return '';
        let localNumber = phone;
        // Sort by length desc to match long codes first
        const sortedCodes = [...COUNTRY_CODES].sort((a, b) => b.length - a.length);
        for (const code of sortedCodes) {
            if (phone.startsWith(code)) {
                localNumber = phone.slice(code.length);
                break;
            }
        }
        return localNumber;
    }, []);

    // Debounced auto-search effect
    // Note: This needs to be called inside the component reusing the hook logic
    // or we can export a text-cleaner or rely on the component to call searchDoctorByPhone

    return {
        isSearching,
        searchComplete,
        searchResults,
        searchDoctorByPhone,
        setSearchComplete,
        getLocalNumber
    };
}
